<template>
    <div id="app" class="fullscreen" v-show="isShowApp">
        <floating-point v-if="appConfig.game_id && !isUserCenter"/>
        <router-view/>
        <bind-id-card
            :closable="bindIDCardClosable"
            :loginOutable="bindIDCardLoginOutable"
            v-if="showBindIDCard"
            @close="onBindIDClose"
            @bind-success="onBindIDSuccess"
            class="depth-5"
            style="position:relative;"
        ></bind-id-card>
        <write-review :is-hide="isHide" />
        <jump-appstore v-if="!isHide" />
        <wx-pay
            v-if="isWxPay"
            :info="wxPayData"
            @on-close="isWxPay = false"
        />
        <pay-type-dialog
            :show="showPayTypeDialog"
            :data="payTypeDialogData"
            @confirm="onPayTypeConfirm"
            @cancel="onPayTypeCancel"
        />
    </div>
</template>

<script>
import { combineURLs, getParams, getParamsByUrl, translateUrlParams } from '@/utils/url'
import { getLocalAccountData, getLocalAppConfig, setLocalAccountData } from '@/utils/storage'
import browser, { platform } from '@/utils/browser'
import * as service from '@/service'
import FloatingPoint from '@/components/FloatingPoint'
import verifyIDMixin from '@/mixins/verifyID.mixin'
// import { errorCollect } from '@/main'
import {
    webkitGetLocalStorage,
    WebkitGetLocalStorageMessage,
    WebkitReceiveLocalStorageMessage,
    webkitSetLocalStorage,
    WebkitSetLocalStorageMessage,
} from '@/utils/webview'
import { mapState } from 'vuex'
import md5 from 'md5'
import { DispatchErrorCollectEvent } from '@/utils/ErrorCollect/utils/CustomErrorEvent'
import { Dialog } from 'vant'
import handleWeChatEnv from '@/utils/handlers/wechatEnv'
import WriteReview from '@/components/WriteReview'
import JumpAppstore from './components/JumpAppstore.vue'
import WxPay from '@/components/WxPay.vue'
import PayTypeDialog from '@/components/PayTypeDialog.vue'
import { $t } from '@/utils/languages'

const ActivePaymentEventKey = 'activePayment'
const ClosePaymentEventKey = 'closePayment'
const ReLoginEventKey = 'reLogin'
const ShowServiceQQEventKey = 'showServiceQQModal'
const ShowSocialMediaModalEventKey = 'showSocialMediaModal'
// const StartChargeEventKey = 'startCharge'
// const ActiveWXJsBridgePaymentEventKey = 'activeWXJsBridgePayment'

const paymentFrameID = 'payment-frame'
/**
 * @typedef WxBridgePayConfig
 * @property  { string } appId  公众号id
 * @property  { string } timeStamp  自1970年以来的秒数
 * @property { string } nonceStr  随机串
 * @property { string } package  订单详情扩展字符串 prepay_id=123456789
 * @property { string } signType  微信签名方式
 * @property { string } paySign  微信签名
 * */

/**
 * @param { WxBridgePayConfig } config  支付配置'
 * @description 调起微信支付
 * @author Carson
 * @date 2021/7/7
 * */

// function onBridgeReady(config) {
//     if (typeof WeixinJSBridge !== 'undefined') {
//         /*eslint no-undef: "off"*/
//         WeixinJSBridge.invoke(
//             'getBrandWCPayRequest',
//             config,
//             function(res) {
//                 console.log('wx bridge pay res', res, config)
//                 if (res.err_msg == 'get_brand_wcpay_request:ok') {
//                     // 使用以上方式判断前端返回,微信团队郑重提示：
//                     //res.err_msg将在用户支付成功后返回ok，但并不保证它绝对可靠。
//                     let paymentFrame = document.querySelector(`#${paymentFrameID}`)
//                     paymentFrame && paymentFrame.contentWindow.postMessage({
//                         key: 'hidePayForm'
//                     }, '*')
//                 }
//             },
//         )
//     }
// }

export default {
    mixins: [verifyIDMixin],
    components: {
        FloatingPoint,
        WriteReview,
        JumpAppstore,
        WxPay,
        PayTypeDialog,
    },
    data() {
        return {
            isHide: false, // 是否隐藏 评论好礼 图标（引流配置也是）
            isUserCenter: false, // 2022.03.31 针对ios直接跳到用户中心的需求
            isShowApp: false,
            isWxPay: false, // 2022.07.27 微信公众号支付
            wxPayData: {}, // 2022.07.27 微信公众号支付参数
            showPayTypeDialog: false, // 是否显示支付方式选择弹窗
            payTypeDialogData: null, // 支付方式弹窗携带的 data
        }
    },
    computed: {
        ...mapState({
            paymentConfig: state => state.app.paymentConfig,
            verifyInfo: state => state.verifyInfo,
            appConfig: state => state.app.appConfig || {},
        }),
    },
    methods: {
        createGame() {
            let frame = document.createElement('iframe')
            frame.classList.add('game-frame', 'depth-5')
            frame.setAttribute('frameborder', '0')
            frame.setAttribute('src', 'https://c.ipyaoguai.com/monster_demo/index.html')
            // frame.setAttribute('sandbox', 'allow-top-navigation allow-scripts allow-same-origin')
            document.body.appendChild(frame)
        },
        // 微信公众号支付
        doWxPay(data) {
            this.wxPayData = data
            this.isWxPay = true
        },
        // 支付方式选择弹窗确认
        onPayTypeConfirm({ type, data }) {
            if (type === 'credit') {
                this.createPayIframe(data)
            } else {
                this.doApplePay(data)
            }
            this.showPayTypeDialog = false
            this.payTypeDialogData = null
        },
        // 支付方式选择弹窗取消
        onPayTypeCancel() {
            this.showPayTypeDialog = false
            this.payTypeDialogData = null
        },
        // 苹果内购支付 / 安卓谷歌原生支付
        doApplePay(data) {
            // 2022.6.30 若存在 ios_applepay苹果内购支付方式，则直接使用苹果支付
            if (window?.webkit?.messageHandlers?.ios_applepay?.postMessage) {
                const {
                    appConfig = getLocalAppConfig(),
                    accountData = getLocalAccountData(),
                } = this.$store.state

                const iosPay = {
                    muid: appConfig.muid || getParamsByUrl('muid', data.value),
                    game_id: appConfig.game_id || getParamsByUrl('game_id', data.value),
                    platform: appConfig.platform,
                    username: accountData.username || getParamsByUrl('username', data.value),
                    user_token: accountData.user_token,
                    reg_time: accountData.reg_time,
                    debug: 'development',
                    is_pc: browser.isPC ? 1 : 0,
                    token_sign: 'JhGame666nbl2V|gfZp{8`;jzR~6Y1_mOtErFer'
                };

                ([
                    'server_id',
                    'server_name',
                    'order_money',
                    'pay_money',
                    'goods_name',
                    'goods_id',
                    'role',
                    'role_id',
                    'data',
                    'ext',
                    'pay_sign',
                    'timestamp',
                ]).forEach(key => {
                    iosPay[key] = getParamsByUrl(key, data.value)
                })

                // 2022.07.06 增加订单签名
                // 订单签名：md5(用户名.ext.商品名.订单金额.游戏id.时间戳)
                iosPay.as = md5(`${iosPay.username || ''}${iosPay.ext || ''}${iosPay.goods_name || ''}${iosPay.order_money || ''}${iosPay.game_id || ''}${iosPay.timestamp || ''}`);

                const iosPayParams = { data: JSON.stringify(iosPay) }
                window.webkit.messageHandlers.ios_applepay.postMessage(iosPayParams)
                console.log('ios_applepay Params: => ', iosPayParams)
                return
            } else if (window?.JsJingYouNativeBridge?.pay) {
                // 2025.05.28 走 安卓谷歌原生支付
                try {
                    const {
                        appConfig = getLocalAppConfig(),
                        accountData = getLocalAccountData(),
                    } = this.$store.state

                    const googlePay = {
                        muid: appConfig.muid || getParamsByUrl('muid', data.value),
                        game_id: appConfig.game_id || getParamsByUrl('game_id', data.value),
                        platform: appConfig.platform,
                        username: accountData.username || getParamsByUrl('username', data.value),
                        user_token: accountData.user_token,
                        debug: 'development',
                        is_pc: browser.isPC ? 1 : 0,
                        reg_time: accountData.reg_time,
                        level: getParamsByUrl('level', data.value) || localStorage.getItem('CURRENT_ROLE_LEVEL'),
                    }

                    const googleFields = [
                        'server_id',
                        'server_name',
                        'order_money',
                        'pay_money',
                        'goods_name',
                        'goods_id',
                        'role',
                        'role_id',
                        'data',
                        'ext',
                        'pay_sign',
                        'timestamp',
                    ]
                    googleFields.forEach(key => {
                        googlePay[key] = getParamsByUrl(key, data.value) ?? ''
                    })

                    // 2025.05.28 回传支付参数给谷歌原生安卓壳包
                    const thePayInfo = JSON.stringify(googlePay)
                    window?.JsJingYouNativeBridge?.pay?.(thePayInfo)
                    console.log('JsJingYouNativeBridge.pay<Android> & google | pay >>> ', thePayInfo)
                } catch (err) {
                    console.error('>>> android & google pay fail', err)
                }
            } else {
                // 不存在调起苹果内购支付方法时，默认使用原有 H5支付
                this.createPayIframe(data)
            }
        },
        /** 直接走安卓支付判断逻辑 */
        paymentByAndroid(data) {
            try {
                const {
                    appConfig = getLocalAppConfig(),
                    accountData = getLocalAccountData(),
                } = this.$store.state

                const googlePay = {
                    muid: appConfig.muid || getParamsByUrl('muid', data.value),
                    game_id: appConfig.game_id || getParamsByUrl('game_id', data.value),
                    platform: appConfig.platform,
                    username: accountData.username || getParamsByUrl('username', data.value),
                    user_token: accountData.user_token,
                    debug: 'development',
                    is_pc: browser.isPC ? 1 : 0,
                    reg_time: accountData.reg_time,
                    level: getParamsByUrl('level', data.value) || localStorage.getItem('CURRENT_ROLE_LEVEL'),
                }

                const googleFields = [
                    'server_id',
                    'server_name',
                    'order_money',
                    'pay_money',
                    'goods_name',
                    'goods_id',
                    'role',
                    'role_id',
                    'data',
                    'ext',
                    'pay_sign',
                    'timestamp',
                ]
                googleFields.forEach(key => {
                    googlePay[key] = getParamsByUrl(key, data.value) ?? ''
                })

                const thePayInfo = JSON.stringify(googlePay)
                window?.JsJingYouNativeBridge?.nativeJingyouPay?.(thePayInfo)
                console.log('JsJingYouNativeBridge.nativeJingyouPay<Android> & pay >>> ', thePayInfo)
            } catch (err) {
                console.error('>>> android & nativeJingyouPay fail', err)
            }
        },
        // 创建支付 iframe
        createPayIframe(data) {
            if (Object.values(this.paymentConfig).filter(item => item && item !== '').length === 0) {
                this.$toast($t('暂未开通充值通道'))
                return false
            } else {
                let paymentFrame = document.createElement('iframe')
                const isWechatEnv = browser.inWxEnv ? 1 : 0
                const chargeCenterUrl = combineURLs(location.origin,`/chargeCenter?${data.value}&isWechatEnv=${isWechatEnv}`)
                paymentFrame.classList.add('payment-frame', 'depth-4')
                paymentFrame.setAttribute('id', paymentFrameID)
                paymentFrame.setAttribute('frameborder', '0')
                paymentFrame.setAttribute('style', 'background-color: #fff')
                paymentFrame.setAttribute('src', chargeCenterUrl)
                console.log('create payment frame')
                document.body.appendChild(paymentFrame)
                console.log('chargeCenterUrl',chargeCenterUrl)
                console.log('hide game frame')
            }
        },
        initListener() {
            const that = this
            const messageListener = async ({ data }) => {
                // 监听并接收frame中游戏的支付信息
                const gameFrame = that.$store.state.gameFrameInfo.frame ||
                    document.getElementById(that.$store.state.gameFrameInfo.id)

                function handleSdkCallback({ callback, result }) {
                    console.log('sending result into gameFrame:', { callback, result })
                    gameFrame && gameFrame.contentWindow.postMessage({
                        callback, result,
                    }, '*')
                }

                function closePayment() {
                    console.log('close payment frame')
                    let paymentFrame = document.querySelector(`#${paymentFrameID}`)
                    paymentFrame && document.body.removeChild(paymentFrame)
                    gameFrame.style.visibility = 'visible'
                }

                if (data.key) {
                    console.log('receive game frame message', data)
                    this.bindIDCardClosable = true
                    switch (data.key) {
                        case ActivePaymentEventKey: {
                            if (data.value) {
                                if (window?.JsJingYouNativeBridge?.nativeJingyouPay) {
                                    // 2025.12.15 直接走安卓支付判断
                                    this.paymentByAndroid(data)
                                    return
                                }
                                // 判断是否为未成年、未实名玩家
                                // 判断是否超过当月充值限制等罗辑
                                console.log('is_verified', this.verifyInfo.is_verified)
                                this.updateVerifyInfo({
                                    ...this.verifyInfo,
                                    anti: 2,
                                }).then(async () => {
                                    // 2022.07.18 切支付查询
                                    try {
                                        const changePayInfo = await service.getPayType({
                                            gid: getParamsByUrl('game_id', data.value),
                                            rl: localStorage.getItem('CURRENT_ROLE_LEVEL'),
                                            username: getParamsByUrl('username', data.value),
                                        })
                                        if (changePayInfo?.data?.refreshed == '2') {
                                            this.payTypeDialogData = data
                                            this.showPayTypeDialog = true
                                            return
                                        }
                                        if (changePayInfo?.data?.is_apple_pay == '1') {
                                            // 苹果内购支付
                                            this.doApplePay(data)
                                        } else if (changePayInfo?.data?.is_jsapi_pay == '1') {
                                            // 微信公众号支付
                                            // this.doWxPay(data)
                                        } else if (changePayInfo?.data?.is_h5_pay == '1') {
                                            // H5支付（原有的 H5支付）
                                            this.createPayIframe(data)
                                        } else {
                                            // 默认也是 H5支付
                                            this.createPayIframe(data)
                                        }
                                    } catch (err) {
                                        // 切支付查询失败
                                        console.log(err)
                                    }
                                })
                            }
                            break
                        }
                        case ClosePaymentEventKey: {
                            closePayment()
                            break
                        }
                        case ShowServiceQQEventKey: {
                            if (data.value === true) {
                                this.$store.dispatch('app/updateShowServiceQQ', true)
                            }
                            break
                        }
                        case ShowSocialMediaModalEventKey: {
                            if (data.value === true) {
                                this.$store.dispatch('app/updateShowSocialMediaModal', true)
                            }
                            break
                        }
                        case ReLoginEventKey: {
                            //清除保存的token
                            setLocalAccountData({});
                            // todo 需要判断是否打开了游戏
                            if (gameFrame) {
                                this.$router.push({
                                    path: '/redirect',
                                    query: {
                                        redirect: '/login',
                                        ...this.$route.query,
                                        ...translateUrlParams(),
                                        ...getLocalAppConfig(),
                                    },
                                    replace: true,
                                }).then(() => {
                                    gameFrame && document.body.removeChild(gameFrame)
                                    this.$store.state.gameFrameInfo.frame = null
                                    let paymentFrame = document.querySelector(`#${paymentFrameID}`)
                                    paymentFrame && document.body.removeChild(paymentFrame)
                                    this.$store.dispatch('closeGame')
                                })
                            }
                            break
                        }
                        // case StartChargeEventKey: {
                        //     // 20210413 支付宝界面调整，会先认证在跳转，避免过早关闭将窗口时间延长到10s
                        //     console.log('charget value', data.value)

                        //     if (data.value && data.value.payType === 'ali') {
                        //         // let exitTimer = window.exitTimer || null
                        //         // let tipsTimer = window.tipsTimer || null
                        //         const setExitPayment = () => {
                        //             if (window.exitTimer) {
                        //                 clearTimeout(window.exitTimer)
                        //             }
                        //             window.exitTimer = setTimeout(() => {
                        //                 closePayment()
                        //                 clearTimeout(window.exitTimer)
                        //                 clearTimeout(window.tipsTimer)
                        //                 Dialog.close()
                        //             }, 13000)
                        //         }
                        //         const showTips = () => {
                        //             if (window.tipsTimer) {
                        //                 clearTimeout(window.tipsTimer)
                        //             }
                        //             window.tipsTimer = setTimeout(() => {
                        //                 let paymentFrame = document.querySelector(`#${paymentFrameID}`)
                        //                 if (paymentFrame) {
                        //                     this.$dialog.confirm({
                        //                         title: '提示',
                        //                         message: '登录窗口即将过期（5秒后自动关闭），是否继续充值？',
                        //                         confirmButtonText: '继续充值',
                        //                         cancelButtonText: '立即关闭',
                        //                         beforeClose: (action, done) => {
                        //                             if (action === 'confirm') {
                        //                                 showTips()
                        //                                 setExitPayment()
                        //                             } else {
                        //                                 closePayment()
                        //                                 clearTimeout(window.exitTimer)
                        //                                 clearTimeout(window.tipsTimer)
                        //                                 Dialog.close()
                        //                             }
                        //                             done()
                        //                         },
                        //                     })
                        //                 } else {
                        //                     clearTimeout(window.tipsTimer)
                        //                 }
                        //             }, 8000)
                        //         }
                        //         showTips()
                        //         setExitPayment()
                        //     } else {
                        //         setTimeout(closePayment, 12000)
                        //     }
                        //     break
                        // }
                        // case ActiveWXJsBridgePaymentEventKey: {
                        //     console.log('activeWxJsBridgePayment', data.value)
                        //     const startWCPay = () => {
                        //         onBridgeReady(data.value)
                        //     }
                        //     if (typeof WeixinJSBridge === 'undefined') {
                        //         if (document.addEventListener) {
                        //             document.addEventListener('WeixinJSBridgeReady', startWCPay, false)
                        //         } else if (document.attachEvent) {
                        //             document.attachEvent('WeixinJSBridgeReady', startWCPay)
                        //             document.attachEvent('onWeixinJSBridgeReady', startWCPay)
                        //         }
                        //     } else {
                        //         startWCPay()
                        //     }
                        //     break
                        // }
                        case WebkitGetLocalStorageMessage: {
                            console.log('WebkitGetLocalStorageMessage value', data.value)
                            // 发送消息给iOS壳包主体，获取存储过的数据
                            webkitGetLocalStorage(data.value)

                            // 处理iOS返回的结果
                            const listenWebkitResponse = ({ data }) => {
                                if (data.key === '__webkit_parent_receive_local_storage__') {
                                    console.log('receive message from ios ', data.value)
                                    const finalResult = data.value
                                    console.log('final Result: ', finalResult)
                                    gameFrame && gameFrame.contentWindow.postMessage({
                                        key: WebkitReceiveLocalStorageMessage,
                                        value: finalResult,
                                    }, '*')
                                    console.warn('message send')
                                }

                                window.removeEventListener('message', listenWebkitResponse)
                            }

                            window.addEventListener('message', listenWebkitResponse, false)
                            // gameFrame && gameFrame.contentWindow.postMessage({
                            //     key: WebkitReceiveLocalStorageMessage,
                            //     value: webkitGetLocalStorage(data.value)
                            // }, '*')
                            break
                        }
                        case WebkitSetLocalStorageMessage: {
                            console.log('WebkitSetLocalStorageMessage value', data.value)
                            webkitSetLocalStorage(data.value.key, data.value.value)
                            break
                        }
                        default: {
                            console.log('receive message data without handle: ', data)
                            break
                        }
                    }
                }

                if (data.api) {
                    if (service[data.api]) {
                        console.log('using v2 api', data.api)
                        const apiParams = data.params || {}
                        const routeQuery = that.$route.query
                        const result = await service[data.api]({
                            ...routeQuery,
                            ...apiParams,
                        })
                        handleSdkCallback({
                            callback: data.api,
                            result,
                        })
                    } else {
                        handleSdkCallback({
                            callback: data.api,
                            result: {
                                message: '暂无该接口，请检查',
                            },
                        })
                    }
                }

                // 2022.03.24 监听游戏内点击登录的操作（对应CP调用SDK的活跃上报接口）
                if (data.api === 'collectGameActive') {
                    this.isHide = true
                }

                // 2022.04.11 回传下列事件给安卓端：1：创建角色 2：进入游戏  3：角色升级
                try {
                    switch (data?.api) {
                        case 'createNewRole': { // 创建角色（创角上报）
                            const roleInfo = JSON.stringify({ ...data.params, submitType: '1' })
                            window?.JHJsImpl?.submitRoleInfo?.(roleInfo)
                            console.log('JHJsImpl.submitRoleInfo<Android> | createNewRole >>> ', roleInfo)

                            // 2025.09.14 回传ios - 创角
                            const iosRoleInfo = JSON.stringify({ ...data.params })
                            window?.webkit?.messageHandlers?.ios_applerole?.postMessage?.(iosRoleInfo)
                            console.log('webkit.messageHandlers<ios> | ios_applerole.postMessage >>> ', iosRoleInfo)

                            // 2024.01.16 安卓新壳包
                            const theRoleInfo = JSON.stringify({
                                scene: 'createRole',
                                roleName: data?.params?.role ?? '',
                                roleId: data?.params?.role_id ?? '',
                                serverName: data?.params?.server_name ?? '',
                                serverId: data?.params?.server_id ?? '',
                            })
                            window?.JsJingYouNativeBridge?.jsJingYouReport?.(theRoleInfo)
                            console.log('JsJingYouNativeBridge.jsJingYouReport<Android> | createRole >>> ', theRoleInfo)
                            break
                        }
                        case 'collectGameActive': { // 进入游戏（即激活上报、活跃上报）
                            const activeInfo = JSON.stringify({ ...data.params, submitType: '2' })
                            window?.JHJsImpl?.submitRoleInfo?.(activeInfo)
                            console.log('JHJsImpl.submitRoleInfo<Android> | collectGameActive >>> ', activeInfo)

                            // 2024.01.16 安卓新壳包
                            const theActiveInfo = JSON.stringify({
                                scene: 'enterGame',
                                roleName: data?.params?.role ?? '',
                                roleId: data?.params?.role_id ?? '',
                                serverName: data?.params?.server_name ?? '',
                                serverId: data?.params?.server_id ?? '',
                            })
                            window?.JsJingYouNativeBridge?.jsJingYouReport?.(theActiveInfo)
                            console.log('JsJingYouNativeBridge.jsJingYouReport<Android> | enterGame >>> ', theActiveInfo)
                            break
                        }
                        case 'collectRoleLevel': { // 角色升级（特定等级上报）
                            const levelInfo = JSON.stringify({ ...data.params, submitType: '3' })
                            window?.JHJsImpl?.submitRoleInfo?.(levelInfo)
                            console.log('JHJsImpl.submitRoleInfo<Android> | collectRoleLevel >>> ', levelInfo)
                            // 2022.08.02 保存当前角色等级
                            localStorage.setItem('CURRENT_ROLE_LEVEL', data?.params?.level)

                            // 2024.01.16 安卓新壳包
                            const theLevelInfo = JSON.stringify({
                                scene: 'onUpgrade',
                                roleName: data?.params?.role ?? '',
                                roleId: data?.params?.role_id ?? '',
                                serverName: data?.params?.server_name ?? '',
                                serverId: data?.params?.server_id ?? '',
                                roleLevel: data?.params?.level ?? '',
                            })
                            window?.JsJingYouNativeBridge?.jsJingYouReport?.(theLevelInfo)
                            console.log('JsJingYouNativeBridge.jsJingYouReport<Android> | onUpgrade >>> ', theLevelInfo)
                            break
                        }
                        default:
                            break
                    }
                } catch(err) {
                    console.log('回传安卓端失败：', err)
                }

                // 2024.01.18 回传支付 安卓新壳包
                if (data?.key === 'JsJingYouNativeBridgeOfPayInfo') {
                    const query = data?.value || {}
                    let loopNum = 0 // 最多8次
                    const loopFunc = async () => new Promise((resolve) => {
                        setTimeout(async () => {
                            const resInfo = await service.queryOrderStatus({ game_id: query.game_id, ext: query.ext })
                            loopNum = loopNum + 1
                            if (resInfo?.data?.need_report === 'wait' && loopNum < 8) {
                                resolve(await loopFunc())
                            } else if (resInfo?.data?.need_report === 'need') {
                                resolve({ need_report: 'need', pay_money: resInfo.data.pay_money })
                            } else if (resInfo?.data?.need_report === 'noNeed') {
                                resolve({ need_report: 'noNeed' })
                            } else if (loopNum < 8) {
                                resolve(await loopFunc())
                            } else {
                                resolve({})
                            }
                        }, 30 * 1000)
                    })
                    // 开始轮询
                    const theRes = await loopFunc()
                    if (theRes?.need_report === 'need') {
                        // 回传支付参数给新安卓壳包
                        const thePayInfo = JSON.stringify({
                            money: theRes?.pay_money ?? '',
                            goodsId: query?.goods_id ?? '',
                            goodsNum: '1',
                            goodName: query?.goods_name ?? '',
                            paytype: query?.thePaytype ?? '',
                        })
                        window?.JsJingYouNativeBridge?.jsJingYouPay?.(thePayInfo)
                        console.log('JsJingYouNativeBridge.jsJingYouPay<Android> | pay >>> ', thePayInfo)

                        // 2025.09.14 回传ios壳包 - 支付
                        const moneyStr = `${theRes?.pay_money ?? ''}`
                        window?.webkit?.messageHandlers?.ios_appledata?.postMessage?.(moneyStr)
                        console.log('webkit.messageHandlers<ios> | ios_appledata.postMessage >>> ', moneyStr)
                    }
                }

                // 2025.06.24 外链打开 Payermax支付
                // if (data?.key === 'JsBridgeOfPayerIOS') {
                //     try {
                //         console.log('>>>> start payermax', data)
                //         let payermaxFrame = document.createElement('iframe')
                //         payermaxFrame.classList.add('payment-frame', 'depth-4')
                //         payermaxFrame.setAttribute('id', 'payermax-iframe')
                //         payermaxFrame.setAttribute('frameborder', '0')
                //         payermaxFrame.setAttribute('style', 'background-color: #fff')
                //         payermaxFrame.setAttribute('src', data.value)
                //         document.body.appendChild(payermaxFrame)
                //         console.log('>>>> end payermax', data)
                //         setTimeout(() => {
                //             document.getElementById('payermax-iframe').style.display = 'none';
                //         }, 10 * 1000)
                //     } catch (err) {
                //         console.error(err)
                //     }
                // }
            }
            window.addEventListener('message', messageListener, false)
        },
    },
    async mounted() {
        // 处理微信浏览器环境获取author code
        // 移除资源加载检测计时器
        window.removeLoadingTimer && window.removeLoadingTimer()
        // 移除资源加载遮罩层
        window.removeLoadingMask && window.removeLoadingMask()
        // this.createGame()
        const localAppConfig = getLocalAppConfig()
        const localAccountData = getLocalAccountData()
        // this.$store.dispatch('updateAppConfig', localAppConfig)
        let muidValue = getParams('muid')
        const UrlGameID = getParams('game_id')
        this.isUserCenter = getParams('is_user_center') // 2022.03.31 针对ios直接跳到用户中心的需求

        /**
         * patch 修复 1073002 游戏由于iOS壳包缺少muid的问题
         * @author Carson
         * @date 2021/1/28
         * */
        const errorGameList = ['1073002']
        if (errorGameList.includes(UrlGameID)) {
            console.log('err g')
            const errorGameID = UrlGameID
            const allParams = translateUrlParams(window.location.search, true)
            const emptyParams = Object.entries(allParams).filter(([k, v]) => !v).map(([k]) => k)
            console.log('emptyParams', emptyParams)
            // 缺少MUID
            if (!muidValue && emptyParams.length) {
                const fallbackMuid = emptyParams.find(k => {
                    // 寻找符合 包括 - 且 长度大于30的键值
                    return /.{30,}/g.test(k) && /(-)/g.test(k)
                })
                console.log('fallback muid',
                    `${fallbackMuid.substring(0, 5)}*****${fallbackMuid.substring(fallbackMuid.length - 5)}`)
                fallbackMuid && (muidValue = fallbackMuid)
            }
        }

        const appConfig = {
            bg: getParams('bg') || localAppConfig.bg || '',
            game: getParams('game') || localAppConfig.game || '',
            muid: muidValue || localAppConfig.muid || '',
            game_id: UrlGameID || localAppConfig.game_id,
            platform,
            username: getParams('username') || localAppConfig.username,
            bak: getParams('bak') || localAppConfig.bak,
            model: getParams('model') || localAppConfig.model || '',
        }
        console.log('appConfig', appConfig)
        /* 获取配置信息 */
        let viewportContent = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
        const viewPortEle = document.querySelector('meta[name="viewport"]')
        const noFitScreen = [] // 2023.04.03 不需要SDK适配全面屏（即游戏内已经做了适配）noFitScreen = ['1113014', '1113006']
        if (!(/^1073/g.test(appConfig.game_id) || noFitScreen.includes(`${appConfig?.game_id}`))) {
            document.body.classList.add('compatible-ios')
            viewportContent = viewportContent + ', viewport-fit=cover'
        }
        viewPortEle.setAttribute('content', viewportContent)
        //先存储一遍，确保可以先加载背景图
        this.$store.dispatch('app/updateAppConfig', appConfig)
        if (appConfig.game_id) {
            if (muidValue && muidValue !== localAppConfig.muid && !this.isUserCenter) {
                service.collectActive({
                    muid: muidValue,
                    game_id: appConfig.game_id,
                    platform: appConfig.platform,
                })
            }

            console.log('init')
            
            let res = await service.initH5({ game_id: appConfig.game_id })
            console.log("initData",res)
            if(res.code == 0){
                this.$store.dispatch('app/initH5', res.data)
                console.log("inner_wx_appid",res.data.inner_wx_appid)

                //判断后台是否配置公众号APPID，有就进行微信网页授权
                if(res.data.inner_wx_appid){
                    handleWeChatEnv(res.data.inner_wx_appid)
                }
            }

            if (res.code != 520) {
                // 2022.6.30 按后端需求，状态码为 520时，直接让页面空白，其他状态码正常显示页面
                this.isShowApp = true
            }

            console.log('end init')
            this.$store.dispatch('app/updateAppConfig', appConfig)
            this.$store.dispatch('updateAccountData', localAccountData)
            this.$store.dispatch('getGameService')
            this.$store.subscribeAction({
                after: (action) => {
                    if (action === 'saveAccountData') {
                        /* 检查是否已绑定手机号 */
                    }
                },
            })
            // errorCollect.updateBaseData({
            //     appConfig,
            //     accountData: localAccountData,
            // })
            this.initListener()
        } else {
            this.$dialog.alert({
                title: '温馨提示',
                message: '初始化失败\n请更新或重新下载游戏',
            })
            DispatchErrorCollectEvent({
                error_msg: '初始化失败',
                error_type: 'H5_INIT_FAIL',
                error_detail: appConfig,
            })
        }

        // 当发现链接参数有open_debug且为1是开启vConsole
        const openDebugMode = getParams('open_debug')
        if (Number(openDebugMode) === 1) {
            // openVConsole()
        }
    },
}
</script>
<style>
/* 设置全屏样式 */
#app {
    height: 100vh;
    width: 100vw;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
    overflow: hidden;
}

/**
* 游戏层：3
* 支付层：4
* 浮窗、浮点：5
*/

.depth-default {
    z-index: auto;
}

.depth-1 {
    z-index: 1001;
}

.depth-2 {
    z-index: 1002;
}

.depth-3 {
    z-index: 1003;
}

.depth-4 {
    z-index: 1004;
}

.depth-5 {
    z-index: 1005;
}

:root {
    --animate-duration: 1s;
    --animate-delay: 1s;
    --animate-repeat: 1
}

.animate__animated {
    animation-duration: 1s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both
}

@-webkit-keyframes fadeInDown {
    0% {
        opacity: 0;
        -webkit-transform: translate3d(0, -100%, 0);
        transform: translate3d(0, -100%, 0)
    }

    to {
        opacity: 1;
        -webkit-transform: translateZ(0);
        transform: translateZ(0)
    }
}

@keyframes fadeInDown {
    0% {
        opacity: 0;
        -webkit-transform: translate3d(0, -100%, 0);
        transform: translate3d(0, -100%, 0)
    }

    to {
        opacity: 1;
        -webkit-transform: translateZ(0);
        transform: translateZ(0)
    }
}

.animate__fadeInDown {
    -webkit-animation-name: fadeInDown;
    animation-name: fadeInDown
}

.game-frame-container {
    -webkit-overflow-scrolling: touch; /* 当手指从触摸屏上移开，会保持一段时间的滚动 */
    overflow-y: scroll;
}

.game-frame {
    box-sizing: border-box;
    height: 100%;
    width: 100vw;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: #000;
}

.payment-frame {
    height: 70vh;
    width: 100vw;
    position: fixed;
    top: 15vh;
    left: 0;
}
</style>
